import * as React from 'react';
import Typography from '@mui/material/Typography';
import { Order } from '../models/OrdersResponse';
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { httpPost, APIEndpoint, EndpointType } from "../utils/apiService";
import Alert from '@mui/material/Alert';
import { ExportOrderRequest, ExportOrderResponse, HighLevelExportSettings } from "../models/ExportOrder"
import { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';

interface Props {
    Order?: Order,
}

interface GoHighLevelSettings {
    apiKey: string,
    pipelineName: string,
    stageName: string,
}


export default function ExportToHighIQ(props: Props) {
    const [settings, setSettings] = useState<GoHighLevelSettings | null>(null)
    const [exportErrorVisibility, setExportErrorVisibility] = useState("none")
    const [exportSuccessVisibility, setExportSuccessVisibility] = useState("none")
    const [errorMessage, setErrorMessage] = useState("")
    const [exportButtonDisabled, setExportButtonDisabled] = useState(false)

    // old local settings key: "highLevelApiKey"
    useEffect(() => {
        const settings = localStorage.getItem("go-high-level-settings")
        if (settings != null) {
            setSettings(JSON.parse(settings))
        }
    })

    const submitExport = () => {
        if (settings == null) {
            setErrorMessage("The API Key must be specified!")
            setExportErrorVisibility("flex")
            return
        }
        
        if (settings?.apiKey.trim() == "") {
            setErrorMessage("The API Key must be specified!")
            setExportErrorVisibility("flex")
            return
        }

        if (settings?.pipelineName.trim() == "") {
            setErrorMessage("The Pipeline Name must be specified!")
            setExportErrorVisibility("flex")
            return
        }

        if (settings?.stageName.trim() == "") {
            setErrorMessage("The Stage Name must be specified!")
            setExportErrorVisibility("flex")
            return
        }

        setExportErrorVisibility("none")
        const exportRequest = {} as ExportOrderRequest
        if (props.Order) {
            exportRequest.orderID = props.Order.id!
        }
        exportRequest.destination = "highiq"

        const exportSettings = {} as HighLevelExportSettings
        exportSettings.normalizeAddresses = true
        exportSettings.apiKey = settings?.apiKey!.trim() || ""
        exportSettings.pipelineName = settings?.pipelineName!.trim() || ""
        exportSettings.stageName = settings?.stageName!.trim() || ""
        setExportButtonDisabled(true)
        setExportSuccessVisibility("none")
        exportRequest.highLevelExportSettings = exportSettings

        const apiURL = APIEndpoint(EndpointType.ExportOrder);
        httpPost(apiURL, JSON.stringify(exportRequest))
            .then((data) => {
                const exportOrderResponse = data as ExportOrderResponse;

                if (exportOrderResponse.status == 'error') {
                    setExportErrorVisibility("flex")
                    setErrorMessage(exportOrderResponse.errorMessage)
                    setExportButtonDisabled(false)
                } else {
                    setExportErrorVisibility("none")
                    setExportSuccessVisibility("flex")
                    // Save location/api key to local storage
                    localStorage.setItem("go-high-level-settings", JSON.stringify(settings))
                }
            })
            .catch((error) => {
                setErrorMessage(error + "")
                setExportErrorVisibility("flex")
                setExportButtonDisabled(false)
            });
    }

    const apiKeyChanged = (event: React.ChangeEvent<{ value: string }>) => {
        const newSettings = settings || {} as GoHighLevelSettings
        newSettings.apiKey = event.target.value
        setSettings(newSettings)
    }

    const pipelineNameChanged = (event: React.ChangeEvent<{ value: string }>) => {
        const newSettings = settings || {} as GoHighLevelSettings
        newSettings.pipelineName = event.target.value
        setSettings(newSettings)
    }

    const stageNameChanged = (event: React.ChangeEvent<{ value: string }>) => {
        const newSettings = settings || {} as GoHighLevelSettings
        newSettings.stageName = event.target.value
        setSettings(newSettings)
    }

    const leadCount = () => {
        let count = 0
        if (props.Order?.products != undefined) {
            for (const product of props.Order.products) {
                count += product.quantity
            }
        }

        return count
    }

    const customTheme = createTheme({
        palette: {
            primary: {
                main: '#1976d2', // Blue
            },
            secondary: {
                main: '#dc004e', // Red
            },
        },
    });

    return (
        <Stack>
            <ThemeProvider theme={customTheme}>
            <Box sx={{  }}>All {leadCount()} leads in this order will be sent to GoHighLevel.</Box>
            <Box sx={{ minWidth: 120, marginRight: 1 }}>
                <FormControl sx={{ marginTop: 3 }} fullWidth>
                    <Stack sx={{ marginTop: 1 }}>
                        <TextField sx={{}} InputLabelProps={{ sx: { color: 'black' } }} label="API Key" variant="outlined" onChange={apiKeyChanged} value={settings?.apiKey} />
                        <Typography sx={{ color: '#000000' }} variant="caption">The API Key is an identifier associated with your GoHighLevel account. To obtain this value, login to your GoHighLevel account, select 'Settings' in the bottom-left of the screen and then look in the 'General Information' section at the bottom. The API Key will be obscured by asterisks so you will need to click on the clipboard icon to the right to copy it.</Typography>
                        <TextField sx={{ marginTop: 3 }} InputLabelProps={{ sx: { color: 'black' } }} label="Pipeline Name" variant="outlined" onChange={pipelineNameChanged} value={settings?.pipelineName} />
                        <Typography sx={{ color: '#000000' }} variant="caption">The Pipeline Name is the name of the sales pipeline in GoHighLevel where you want leads to be added. The name must match exactly.</Typography>
                        <TextField sx={{ marginTop: 3 }} InputLabelProps={{ sx: { color: 'black' } }} label="Stage Name" variant="outlined" onChange={stageNameChanged} value={settings?.stageName} />
                        <Typography sx={{ color: '#000000' }} variant="caption">The Stage Name is the name of the stage in the selected pipeline where you want leads to be added. The name must match exactly.</Typography>
                    </Stack>
                    <Box sx={{ marginTop: 3, display: 'flex', flexDirection: 'row-reverse' }}>
                        <Button variant="contained" onClick={submitExport} disabled={exportButtonDisabled}>Start Export</Button>
                        <Alert severity="error" sx={{ display: exportErrorVisibility, marginRight: 3 }}>{errorMessage}</Alert>
                        <Alert severity="success" sx={{ display: exportSuccessVisibility, marginRight: 3 }}>Success! The export is now running. You will receive an email when the export has completed.</Alert>
                    </Box>
                </FormControl>
            </Box>
            </ThemeProvider>
        </Stack>
    );
}
